import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  CashAccountForClientRequest,
  CashAccountRecordsResponse,
  CashAccountsResponse,
  CashStatementLine,
  DownloadCashStatementsPayload,
  FetchCashStatementsPayload,
  FetchCashStatementsResult,
} from './types';
import api from 'src/app/api';
import { RootState } from 'src/reducers';
import { PagedResult } from 'src/store';
import { buildEncodedQueryString, convertToCSVFormat, CSVDataType, downloadCsv } from 'src/common';

export enum CashAccountsActionTypes {
  FetchCashAccounts = '@@client/bff/FetchCashAccounts',
  FetchCashStatements = '@@client/bff/FetchCashStatements',
  DownloadCashStatements = '@@client/bff/DownloadCashStatements',
}

export enum CashAccountsApiEndpoints {
  FetchCashAccounts = '/bff/GetCashAccountsForClient',
  FetchCashStatements = '/bff/GetCashAccountRecordsForClient',
}

export const fetchCashAccounts = createAsyncThunk(
  CashAccountsActionTypes.FetchCashAccounts,
  async (request: CashAccountForClientRequest) => {
    const queryString = buildEncodedQueryString({ clientId: request.clientId, currenciesForAccountId: request.currenciesForAccountId });

    const response = await api.get<CashAccountsResponse>(`${CashAccountsApiEndpoints.FetchCashAccounts}${queryString}`);
    return response.data?.cashAccounts ?? [];
  },
  {
    condition: (_request: CashAccountForClientRequest, { getState }) =>
      !(getState() as RootState).loading?.[CashAccountsActionTypes.FetchCashAccounts],
  }
);

export const fetchCashStatements = createAsyncThunk(CashAccountsActionTypes.FetchCashStatements, async (wrapper: FetchCashStatementsPayload) => {
  const { results, lastDataRefresh } = await getCashStatements(wrapper);

  return {
    results,
    pagination: wrapper.pagination,
    lastDataRefresh,
  } as FetchCashStatementsResult;
});

export const downloadCashStatements = createAsyncThunk(
  CashAccountsActionTypes.DownloadCashStatements,
  async (wrapper: DownloadCashStatementsPayload) => {
    const { results } = await getCashStatements(wrapper);
    const data: CSVDataType[] = convertToCSVFormat(wrapper.mapper(results.results), true);

    downloadCsv(data, `Client_cash_accounts_`);

    return { message: 'CSV downloaded' };
  }
);

const getCashStatements = async (
  request: FetchCashStatementsPayload
): Promise<{ results: PagedResult<CashStatementLine>; lastDataRefresh: string | null }> => {
  const { clientId, parameters } = request.inputs;
  const body = {
    clientId,
    accountId: parameters.selectedAccountId,
    securityId: parameters.selectedSecurityId,
    fromDate: parameters.dates.dateFrom,
    toDate: parameters.dates.dateTo,
    queryFields: request.pagination.queryFields,
  };

  const response = await api.post<CashAccountRecordsResponse>(CashAccountsApiEndpoints.FetchCashStatements, body);

  const accountId = response.data?.accountId;
  const lines: CashStatementLine[] = (response.data?.records || []).map((record, index) => ({
    id: index,
    accountId,
    date: record.date,
    systemTransactionType: record.systemTransactionType,
    description: record.description,
    // Old transactions created before native amounts were introduced will not have native amounts, so we fallback to the normal amounts.
    // If native amounts are available, we want to show them because they reflect the actual currency of the transaction.
    credit: record.nativeCredit ?? record.credit,
    debit: record.nativeDebit ?? record.debit,
    balance: record.nativeBalance ?? record.balance,
    balanceAud: record.balance,
  }));

  return {
    results: {
      pageNumber: 1,
      pageSize: lines.length,
      totalNumberOfPages: 1,
      totalNumberOfRecords: lines.length,
      results: lines,
    },
    lastDataRefresh: response.data?.lastDataRefresh ?? null,
  };
};
